import { useEffect, useState } from "react";
import { Clock, Loader2, ChevronDown, ChevronRight, Mail, CalendarCog } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { ShiftScheduleDialog } from "@/components/ShiftScheduleDialog";

/** First and last time each person was seen at work today (Teams presence +
 *  Intercom cross-check), with late starts flagged. All times MDT. */
export const ShiftTimesCard = ({ team = "" }) => {
  const { has } = useAuth();
  // Iter 286 — `inout.edit` opens the Shifts editor; the backend scopes it to
  // the caller's own team(s) unless they are a super admin.
  const canEditShifts = has("inout.edit");
  const [when, setWhen] = useState("today");     // today | yesterday
  const [autoFell, setAutoFell] = useState(false);   // auto-fell back to yesterday
  const [manual, setManual] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailing, setEmailing] = useState(false);
  const [shiftsOpen, setShiftsOpen] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem("shift-times-open") !== "0"; } catch { return true; }
  });
  const toggle = () => setOpen((v) => {
    const nv = !v;
    try { localStorage.setItem("shift-times-open", nv ? "1" : "0"); } catch { /* ignore */ }
    return nv;
  });

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get(`/inout/shift-report?preset=${when}${team ? `&teams=${encodeURIComponent(team)}` : ""}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [open, when, team, reloadKey]);

  const emailMe = async () => {
    setEmailing(true);
    try {
      const r = await api.post("/inout/shift-report/email", { preset: when, teams: team || "" });
      if (r.data?.status === "sent") toast.success(`Shift email sent to ${r.data.to}`);
      else if (r.data?.status === "paused") toast.warning("Email is paused platform-wide — unpause it on the Dashboard banner to receive the test.");
      else toast.error("Couldn't send the shift email.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the shift email.");
    } finally {
      setEmailing(false);
    }
  };

  // A fresh MDT day has no readings until the first Teams/Intercom event, so
  // fall back to the previous day rather than showing an empty card.
  useEffect(() => {
    if (data && when === "today" && (data.seen_count || 0) === 0 && !manual && !autoFell) {
      setAutoFell(true);
      setWhen("yesterday");
    }
  }, [data, when, manual, autoFell]);

  const rows = (data?.rows || []).filter((r) => r.start);
  const tz = data?.tz_abbr || "MDT";

  return (
    <div className="rounded-xl border border-border bg-card shadow-sm" data-testid="shift-times-card">
      <button type="button" onClick={toggle} data-testid="shift-times-toggle"
        className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/40 ${open ? "border-b border-border" : ""}`}>
        {open ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        <Clock className="h-4 w-4 text-primary" />
        <span className="text-sm font-semibold text-foreground">Shift times</span>
        <span className="text-[11px] text-muted-foreground">
          Earliest and latest time seen — all times are in {tz}
        </span>
        {data?.late_count > 0 && (
          <span className="rounded-full bg-rose-500/10 px-2 py-0.5 text-[11px] font-bold text-rose-600 dark:text-rose-300"
                data-testid="shift-times-late-count">
            {data.late_count} late
          </span>
        )}
        {!open && <span className="ml-auto text-[11px] text-muted-foreground">Show</span>}
      </button>

      {open && (
        <>
          <div className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2 text-xs">
            {["today", "yesterday"].map((k) => (
              <button key={k} type="button" onClick={() => { setManual(true); setAutoFell(false); setWhen(k); }}
                data-testid={`shift-times-range-${k}`}
                className={`rounded-full px-2.5 py-1 font-medium capitalize transition-colors ${when === k ? "bg-primary/15 text-primary" : "text-muted-foreground hover:bg-muted"}`}>
                {k}
              </button>
            ))}
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />}
            {autoFell && when === "yesterday" && (
              <span className="text-[11px] text-muted-foreground" data-testid="shift-times-fallback-note">
                No readings yet today — showing the previous day
              </span>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              <span className="text-[11px] text-muted-foreground">
                {data?.date ? `${data.date} · ` : ""}scheduled {data?.default_start || "—"} · {data?.grace_minutes ?? 5} min grace
              </span>
              <Button type="button" size="sm" variant="outline" onClick={emailMe} disabled={emailing || rows.length === 0}
                      className="h-7 gap-1.5 text-[11px]" data-testid="shift-times-email-me">
                {emailing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                Email me this
              </Button>
              {canEditShifts && (
                <Button type="button" size="sm" variant="outline" onClick={() => setShiftsOpen(true)}
                        className="h-7 gap-1.5 text-[11px]" data-testid="shift-times-edit-shifts">
                  <CalendarCog className="h-3.5 w-3.5" /> Shifts
                </Button>
              )}
            </div>
          </div>

          {rows.length === 0 ? (
            <p className="px-4 py-8 text-center text-sm text-muted-foreground" data-testid="shift-times-empty">
              Nobody has been seen at work yet {when === "today" ? "today" : "that day"}.
            </p>
          ) : (
            <ul className="divide-y divide-border" data-testid="shift-times-list">
              {rows.map((r) => (
                <li key={r.user_id} data-testid={`shift-times-row-${r.user_id}`}
                    className="flex flex-wrap items-center gap-3 px-4 py-2">
                  <Avatar name={r.name} src={r.avatar_url} className="h-7 w-7 shrink-0" />
                  <div className="min-w-[140px] flex-1">
                    <div className="truncate text-sm font-medium text-foreground">{r.name}</div>
                    <div className="truncate text-[11px] text-muted-foreground">{r.team || "—"}</div>
                  </div>
                  <span className="font-mono text-xs tabular-nums text-foreground" data-testid={`shift-times-start-${r.user_id}`}>
                    {r.start}
                  </span>
                  <span className="text-[11px] text-muted-foreground">→</span>
                  <span className="font-mono text-xs tabular-nums text-foreground">{r.end || "—"}</span>
                  <span className={`ml-auto rounded-full px-2 py-0.5 text-[10px] font-bold ${r.late ? "bg-rose-500/10 text-rose-600 dark:text-rose-300" : r.approx ? "bg-muted text-muted-foreground" : "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}`}
                        data-testid={`shift-times-status-${r.user_id}`}
                        title={r.sweep
                          ? `Scheduled ${r.scheduled_start} — this was the presence poller's first sweep of the day (several people share this exact time), so it's when we started watching, not a real arrival. Never counted as late.`
                          : r.approx
                            ? `Scheduled ${r.scheduled_start} — only 15-minute snapshots for this day, so the start time is approximate`
                            : `Scheduled ${r.scheduled_start}`}>
                    {r.late ? `late ${r.late_by_minutes}m` : r.approx ? "approx" : "on time"}
                  </span>
                  {r.early_finish && (
                    <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] font-bold text-amber-700 dark:text-amber-300"
                          data-testid={`shift-times-early-${r.user_id}`}
                          title={`Finished ${r.early_by_minutes}m before the scheduled ${r.scheduled_end}`}>
                      left {r.early_by_minutes}m early
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
      <ShiftScheduleDialog open={shiftsOpen} onOpenChange={setShiftsOpen}
                           onSaved={() => setReloadKey((k) => k + 1)} />
    </div>
  );
};

export default ShiftTimesCard;
